import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { itineraryApi } from '@/src/api/rtkQuery/itineraryApi'
import { Flight, PaxPricingInfo, VasAncillary } from '@/src/types/itinerary'
import {
    getFares,
    getInsurances,
    getSelectedInsurance,
    getSelectedTicketedVas,
    getTicketedVasList
} from '@/src/utils/itinerary/flightTransformers'
import { logError } from '@/src/utils/logError'
import getItineraryEndpointPath from '@/src/utils/rtkQuery/getItineraryEndpointPath'
import setCommonHeaders from '@/src/utils/rtkQuery/setCommonHeaders'

// Response shape matches the trip-add / VAS products endpoint.
export type VasProductsResponse = VasAncillary[]

type ItineraryDataDraft = {
    flights?: Flight[]
    faresList?: unknown[][]
    itineraryData?: {
        vasAncillaries?: VasAncillary[]
        paxPricingInfoJson?: PaxPricingInfo[]
    }
    insurance?: {
        insuranceList: VasAncillary[]
        ticketedVasList: VasAncillary[]
        selectedInsurances: VasAncillary[]
        isInsuranceTermsAndConditionsChecked?: boolean
    }
}

/** Same derivation as transformItineraryData (faresList + insurance); runs when VAS API hydrates before reload view-itin. */
function applyVasProductsToItineraryDraft (draft: ItineraryDataDraft, vasProducts: VasAncillary[]) {
    if (!draft?.itineraryData || !vasProducts.length) {
        return
    }

    draft.itineraryData.vasAncillaries = vasProducts

    if (!draft.insurance) {
        draft.insurance = {
            insuranceList: [],
            ticketedVasList: [],
            selectedInsurances: [],
            isInsuranceTermsAndConditionsChecked: true
        }
    }

    const insuranceList = getInsurances(vasProducts)
    const ticketedVasList = getTicketedVasList(vasProducts)
    draft.insurance.insuranceList = insuranceList
    draft.insurance.ticketedVasList = ticketedVasList
    draft.insurance.selectedInsurances = [
        ...getSelectedInsurance(insuranceList),
        ...getSelectedTicketedVas(ticketedVasList)
    ]

    // EzCancel / Flexifly fare rows (VasForFareFamily reads faresList, not insuranceList)
    if (draft.flights) {
        draft.faresList = getFares(
            draft.flights,
            draft.itineraryData.paxPricingInfoJson ?? [],
            vasProducts
        )
    }
}

export const vasApi = createApi({
    reducerPath: 'vasApi',
    baseQuery: fetchBaseQuery({
        prepareHeaders: headers => setCommonHeaders(headers)
    }),

    // Keep unused cached data for 10 minutes — same as other itinerary slices
    keepUnusedDataFor: 600,

    endpoints: builder => ({
        getVasProducts: builder.query<VasProductsResponse, { itineraryId: string }>({
            query: ({ itineraryId }) =>
                getItineraryEndpointPath(itineraryId, `/itin/vas/getVasProducts?itineraryId=${itineraryId}`),
            transformErrorResponse: (response, meta, arg) => {
                const { status, data } = response
                return {
                    status,
                    message: `VAS products request failed with status ${status}`,
                    itineraryId: arg.itineraryId,
                    data: data || {}
                }
            },
            async onQueryStarted ({ itineraryId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    if (!Array.isArray(data) || !data.length) {
                        logError('getVasProducts', { itineraryId, reason: 'empty or non-array response' })
                        return
                    }
                    dispatch(
                        itineraryApi.util.updateQueryData('getItineraryData', { itineraryId }, draft => {
                            applyVasProductsToItineraryDraft(draft, data)
                        })
                    )
                } catch (error) {
                    // Cache stays unchanged and the section falls back to "unavailable", so this
                    // is the only signal that VAS was lost rather than genuinely empty.
                    logError('getVasProducts', { itineraryId, error })
                }
            }
        })
    })
})

export const { useGetVasProductsQuery } = vasApi

export default vasApi
